import * as React from "react";
import { useEffect, useRef } from "react";
import { Skeleton } from "antd5";
import { Flex } from "styles/utility-components";

import { BasicContentBanner } from "components/banners/Banners";
import { RailPageContent } from "components/record_details/notice_insights/NoticeInsightsRail";
import NoticeDetailsContent from "components/record_details/NoticeDetailsContent";
import NoticeHeader from "components/record_details/NoticeHeader";
import EmptyState from "components/ui/empty_state/EmptyState";
import RecordStage from "lib/core_components/RecordStage";
import { NOTICE_INSIGHTS_RAIL, useVariableValue } from "lib/featureFlags";
import { NoticeDetails, useNotice } from "lib/hooks/api/notices/useNotice";
import { useNoticeHighlighting } from "lib/hooks/api/notices/useNoticeHighlighting";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import useElementInViewport from "lib/hooks/useElementInViewport";
import { EventNames, pageView, TrackingProvider, useTracking } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";
import { getSignalTypes, TenderStage } from "lib/types/models";
import { PaywallBanner } from "../../components/paywall/PaywallBanner";

import css from "./NoticeDetailsPage.module.scss";

type NoticeDetailsProps = {
  notice: NoticeDetails;
};

type NoticeDetailsContainerProps = {
  noticeGuid: string;
};

export function NoticeDetailsContainer({ noticeGuid }: NoticeDetailsContainerProps) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get("src") === "email" && currentUser) {
      logEvent(EventNames.emailLinkClicked, {
        email: currentUser.email,
        Link: window.location.href,
      });
    }
  }, [logEvent, currentUser]);

  useLogEntityViewOnOpen(LoggedEntityType.Notice, noticeGuid);

  const { data: notice, isLoading, isError } = useNotice(noticeGuid);

  // Emit the "Notice Details" page view from here (the router subscriber is opted out via the
  // route's empty pageName) so the event carries the Stage/Signal props Amplitude needs to
  // segment award vs tender usage. A ref guards against re-emitting for the same notice, so the
  // event fires exactly once per notice while the effect depends on the whole `notice` object
  // (and it re-fires on SPA navigation to a different notice).
  const trackedNoticeGuid = useRef<string | null>(null);
  useEffect(() => {
    if (!notice || notice.guid === trackedNoticeGuid.current) return;
    trackedNoticeGuid.current = notice.guid;
    // Compensate for the removed router pageName, which previously set the tab title.
    document.title = `${notice.name} | Stotles`;
    const signalTypes = getSignalTypes(notice.signals ?? []);
    pageView("Notice Details", {
      "Record GUID": notice.guid,
      "Signal score": notice.score ?? 0,
      Stage: notice.stage ?? "Unknown",
      "Signal types": signalTypes ? Object.keys(signalTypes) : [],
      Signals: signalTypes,
      path: window.location.pathname,
    });
  }, [notice]);

  if (isLoading) {
    return (
      <div className={css.skeletonWrapper}>
        <Skeleton className={css.skeleton} />
      </div>
    );
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="Sorry, something has gone wrong loading this notice"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!notice) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="startingState"
          heading="No notice found"
          description="The notice you are looking for could not be found"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (notice.redacted) {
    return (
      <div>
        <div className={css.redactedHeader}>
          <RecordStage stage={TenderStage.AWARDED} />
          <h1>{notice.name}</h1>
        </div>
        <PaywallBanner featureType="AWARDS" />
      </div>
    );
  }

  return <NoticeDetailsPage notice={notice} />;
}

function NoticeDetailsPage({ notice }: NoticeDetailsProps) {
  const subscription = useSubscription();

  const [topOfPageRef, topOfPageIsVisible] = useElementInViewport<HTMLDivElement>();

  const signalTypes = getSignalTypes(notice.signals ?? []);

  const { highlightTerms } = useNoticeHighlighting(notice.procurementStage.id);
  const railEnabled = useVariableValue(NOTICE_INSIGHTS_RAIL, false);
  const ContentContainer = railEnabled ? RailPageContent : "div";

  const hasAwards = subscription.hasDataTypes("AWARDS");

  return (
    <TrackingProvider
      data={{
        "Record GUID": notice.guid,
        "Signal score": notice.score ?? 0,
        Stage: notice.stage ?? "Unknown",
        "Signal types": signalTypes ? Object.keys(signalTypes) : [],
        Signals: signalTypes,
      }}
    >
      <div className={css.pageWrapper}>
        {!hasAwards && notice.stage === "AWARDED" && <BasicContentBanner />}
        <NoticeHeader
          notice={notice}
          headerHighlights={highlightTerms?.title || []}
          trackingContextName="Title header"
          disableTitleLink
          headerClassName={css.header}
          scrollClassName={css.scroll}
          showStage={topOfPageIsVisible}
          isTopOfPageVisible={topOfPageIsVisible}
        />
        <div ref={topOfPageRef} />
        <ContentContainer className={railEnabled ? undefined : css.contentContainer}>
          <NoticeDetailsContent
            notice={notice}
            noticeHighlights={highlightTerms}
            trackingContextName="Title header"
            containerClassName={css.columnsContainer}
          />
        </ContentContainer>
      </div>
    </TrackingProvider>
  );
}
